import { SkeletonLoader, formatChip } from '@stern-ui/ui';
import { formatTimeWithTimeZone } from '@stern-ui/utilities';
import { createColumnHelper } from '@tanstack/react-table';

import { DataTableObject, TableConfigType } from '@/components/PatientDataTable';
import { DETAILS_SIDE_PANEL } from '@/components/PatientDataTable/consts';
import { formatDate } from '@/utils';

import { TrialSiteManagementPatientTableRowObject } from '../types';

const columnHelper = createColumnHelper<DataTableObject>();

const columns = [
	columnHelper.accessor('patient_identifier', {
		header: () => 'Patient ID',
		enableSorting: true,
	}),
	columnHelper.accessor('patient_full_name', {
		header: () => 'Name',
		enableSorting: true,
	}),
	columnHelper.accessor('consent_status', {
		header: () => 'Consent',
		cell: ({ row, getValue }) => {
			const isUpdatePending = (row.original as TrialSiteManagementPatientTableRowObject)
				.is_update_pending;
			if (isUpdatePending) {
				return <SkeletonLoader className='h-[29px] rounded-lg w-24' />;
			}
			const { Element } = formatChip(getValue() as string);
			return Element;
		},
		enableSorting: true,
	}),
	columnHelper.accessor('randomisation_group', {
		header: () => 'Randomised',
		cell: ({ row, getValue }) => {
			const isUpdatePending = (row.original as TrialSiteManagementPatientTableRowObject)
				.is_update_pending;
			if (isUpdatePending) {
				return <SkeletonLoader className='h-[29px] rounded-lg w-12' />;
			}
			const value = getValue();
			const label = value === '' ? 'awaiting_randomisation_table' : `${value}_table`;
			const { Element } = formatChip(label as string);
			return Element;
		},
		enableSorting: true,
	}),
	columnHelper.accessor('vaccine_status', {
		header: () => 'Visit Status',
		cell: ({ row, getValue }) => {
			const isUpdatePending = (row.original as TrialSiteManagementPatientTableRowObject)
				.is_update_pending;
			if (isUpdatePending) {
				return <SkeletonLoader className='h-[29px] rounded-lg w-24' />;
			}
			const { Element } = formatChip(getValue() as string);
			return Element;
		},
		enableSorting: true,
	}),
	columnHelper.accessor('next_appointment_datetime', {
		header: () => 'Next Appointment',
		cell: ({ row, getValue }) => {
			const isUpdatePending = (row.original as TrialSiteManagementPatientTableRowObject)
				.is_update_pending;
			if (isUpdatePending) {
				return <SkeletonLoader className='h-[29px] rounded-lg w-36' />;
			}
			return `${formatDate(getValue() as string) ?? ''} ${formatTimeWithTimeZone(getValue() as string) ?? ''}`.trim();
		},
		enableSorting: true,
	}),
	columnHelper.accessor('is_interested_in_flusniff_substudy', {
		header: () => 'Interested in Sub-study?',
		cell: ({ getValue }) => {
			if (getValue() === null) return '';

			const { Element } = formatChip(getValue() === true ? 'sub_study_yes' : 'sub_study_no');
			return Element;
		},
		enableSorting: true,
	}),
];

const rowProps = {
	batchSelect: false,
	rowSelect: true,
	onRowClickTarget: DETAILS_SIDE_PANEL,
	getRowId: (row: DataTableObject) =>
		(row as TrialSiteManagementPatientTableRowObject).patient_id,
};

export const trialSiteManagementPatientTableConfig: TableConfigType = {
	columns: columns,
	rowProps: rowProps,
};
